import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import SuperFetch from 'library/helpers/superFetch';
import { removeEmptyOrNull } from 'library/helpers/utility';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { categoriesActions } from 'reduxStore/categories/slice';
import { sitesActions } from 'reduxStore/sites/slice';
import { Notification } from 'zComponents/atoms';
import { Button, Col, Divider, Form, Input, Modal, Row, Select } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';
import AddCategoryFormWrapper from './AddDeviceCategoryButton.style';
import { theme } from 'antd';

const { TextArea } = Input;

const AddCategoryModal = ({ children }) => {
	const {
		token: { colorTextDescription }
	} = theme.useToken();

	//Hooks
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	//Redux State
	const { data: sitesData, loading: sitePending } = useSelector((state) => state.Sites);
	const { categoryTypes, fieldTypes } = useSelector((state) => state.Types);
	const {
		remoteConfig: { categoryType }
	} = useSelector((state) => state.FbRemoteConfig);

	//Local State
	const [confirmLoading, setConfirmLoading] = useState(false);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (sitesData.length < 1 && sitePending) {
			dispatch(sitesActions.loadSites());
		}
	}, [sitePending, sitesData, dispatch]);

	const handleCancel = () => {
		setVisible(false);
		form.resetFields();
	};

	const handleSubmit = async () => {
		setConfirmLoading(true);
		const formData = removeEmptyOrNull(form.getFieldsValue(true));
		if (formData.isBookable) {
			formData.isBookable = true;
		} else {
			formData.isBookable = false;
		}

		const createCategoryBody = {
			name: formData.name,
			description: formData.description,
			isBookable: formData.typeID === categoryType.BOOKABLE_RESOURCES,
			isDefault: false,
			typeID: formData.typeID
		};

		const createdCategoryID = await SuperFetch.post('/deviceCategories', createCategoryBody).then((res) => {
			if (res.statusCode === 201) {
				Notification('success', 'New Category Created.');
				return res.data.categoryID;
			} else if (res.error.statusCode === 400) {
				Notification('error', 'Please fill out all required information.');
			} else {
				Notification('error', 'There is an Error! We are mending the problem, try again soon!.');
			}
		});
		if (formData.siteID && formData.siteID.length > 0) {
			await SuperFetch.patch(`/deviceCategories/${createdCategoryID}/associations`, {
				siteIDs: formData.siteID
			}).then((res) => {
				if (!res.statusCode === 200) {
					Notification('error', 'There is an Error! We are mending the problem, try again soon!.');
					return false;
				}
			});
		}

		if (createdCategoryID && formData.fields && formData.fields.length > 0) {
			const createFieldBody = { fields: formData.fields };
			await SuperFetch.post(`/deviceCategories/${createdCategoryID}/fields`, createFieldBody).then((res) => {
				if (res.statusCode === 201) {
					Notification('success', 'Fields Added.');
					handleCancel();
				} else if (res.error.statusCode === 400) {
					Notification('error', 'Please fill out all required information.');
				} else {
					Notification('error', 'There is an Error! We are mending the problem, try again soon!.');
				}
				setConfirmLoading(false);
			});
		} else {
			handleCancel();
			setConfirmLoading(false);
		}
		dispatch(categoriesActions.loadCategories());
	};

	return (
		<Fragment>
			<ConditionalTooltipButton buttonText={'Add Category'} onClick={() => setVisible(true)}>
				{children}
			</ConditionalTooltipButton>

			<Modal
				title='Add Category'
				open={visible}
				onCancel={handleCancel}
				width={650}
				destroyOnClose={true}
				footer={[
					<Button key='reset' onClick={handleCancel}>
						Cancel
					</Button>,
					<Button loading={confirmLoading} form={form} key='submit' htmlType='submit' type='primary'>
						Add Category
					</Button>
				]}
			>
				<AddCategoryFormWrapper>
					<Divider className='title subtitle' orientation='left'>
						Category Information
					</Divider>
					<Form
						form={form}
						id={form}
						onFinish={handleSubmit}
						layout='vertical'
						initialValues={{
							typeID: categoryTypes[0] ? categoryTypes[0].typeID : null
						}}
					>
						<Row>
							<Form.Item name='name' label='Name' rules={fieldsValidator.Name} style={{ width: '100%' }}>
								<Input />
							</Form.Item>
						</Row>
						<Row style={{ justifyContent: 'space-between ' }}>
							<Col span={10}>
								<Form.Item
									name='typeID'
									label='Type'
									rules={fieldsValidator.Required}
									style={{ width: '100%' }}
								>
									<Select
										placeholder={'Select Type'}
										options={categoryTypes.map((type) => {
											return { value: type.typeID, label: type.name };
										})}
									/>
								</Form.Item>
							</Col>

							<Col span={10}>
								<Form.Item name='siteID' label='Site' style={{ width: '100%' }}>
									<Select
										mode='multiple'
										placeholder={'Select Site'}
										style={{ fontSize: '14px' }}
										// options={sitesData.map((site) => {
										// 	return { value: site.siteID, label: site.name };
										// })}
										options={[]?.map((site) => ({
											value: site.siteID,
											label: site.name
										}))}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row>
							<Form.Item
								name='description'
								label='Description'
								rules={fieldsValidator.Description}
								style={{ width: '100%' }}
							>
								<TextArea
									style={{ color: colorTextDescription }}
									allowClear
									autoSize={{ minRows: 2, maxRows: 6 }}
								/>
							</Form.Item>
						</Row>

						<Divider className='title subtitle' orientation='left'>
							Category Custom Fields
						</Divider>

						<Form.List name='fields'>
							{(fields, { add, remove }) => (
								<div>
									{fields.map(({ key, name, ...restField }) => (
										<Row
											span={24}
											key={key}
											style={{ display: 'flex', marginBottom: 15 }}
											gutter={10}
										>
											<Col span={16}>
												<Form.Item
													{...restField}
													name={[name, 'title']}
													rules={fieldsValidator.Name}
													style={{ marginBottom: 0 }}
												>
													<Input placeholder='Field Name' />
												</Form.Item>
											</Col>

											<Col span={6}>
												<Form.Item
													{...restField}
													name={[name, 'fieldTypeID']}
													rules={fieldsValidator.Required}
													style={{ marginBottom: 0 }}
												>
													<Select
														placeholder={'Type'}
														options={fieldTypes.map((type) => {
															return { value: type.fieldTypeID, label: type.name };
														})}
													/>
												</Form.Item>
											</Col>

											<Col style={{ alignItems: 'center' }}>
												<Form.Item {...restField} style={{ marginBottom: 0 }}>
													<MinusCircleOutlined onClick={() => remove(name)} />
												</Form.Item>
											</Col>
										</Row>
									))}
									<Form.Item>
										<Button
											type='dashed'
											onClick={() =>
												add({
													fieldTypeID: fieldTypes[0] ? fieldTypes[0].fieldTypeID : null
												})
											}
											block
											icon={<PlusOutlined />}
										>
											Add Custom Field
										</Button>
									</Form.Item>
								</div>
							)}
						</Form.List>

						<Row>
							<Col span={12}>
								{/*	
							<Form.Item
									labelCol={{ span: 10 }}
									wrapperCol={{ span: 12 }}
									label='Bookable'
									name='isBookable'
									style={colStyle}
									rules={[ { required: true } ]}
								>
									<RadioGroup buttonStyle='outline'>
										<RadioButton value={1}>Yes</RadioButton>
										<RadioButton value={0}>No</RadioButton>
									</RadioGroup>
								</Form.Item>
								*/}
							</Col>
						</Row>
					</Form>
				</AddCategoryFormWrapper>
			</Modal>
		</Fragment>
	);
};

export default AddCategoryModal;
